import React, { useState, useEffect, ReactNode } from 'react';
import { Input } from "@/ui-components/ui/input";
import { Label } from "@/ui-components/ui/label";
import { 
  Edit3,
  Type,
  Tag,
} from "lucide-react";
import { toast } from "@/lib/toast";
import { useMediaLibraryRename, useMediaLibraryGetCategories } from "@/lib/api/requests/media-library.requests";
import { MediaLibraryItemDto } from '@/lib/api/interfaces/media-library.interface';
import { CategorySelector } from '@/page-components/post-generator/components/CategorySelector';
import { CreateBaseDialog } from "@/util-components/dialog/base-dialogs/create-base-dialog";

interface EditMediaDialogProps {
  /**
   * The trigger element that opens the dialog (e.g., Button, Icon, etc.)
   */
  trigger?: ReactNode;
  /**
   * Whether the dialog is open
   */
  open?: boolean;
  /**
   * Callback when dialog open state changes
   */
  onOpenChange?: (open: boolean) => void;
  /**
   * The media item to edit
   */
  mediaItem: MediaLibraryItemDto;
  /**
   * Callback function called after successful edit
   */
  onSuccess?: () => void;
}

/**
 * Dialog component for renaming media items
 * Follows the same design principles as CreateTemplateDialog
 */
export const EditMediaDialog: React.FC<EditMediaDialogProps> = ({
  trigger,
  open: externalOpen,
  onOpenChange,
  mediaItem,
  onSuccess,
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);

  // Use external open state if provided, otherwise use internal state
  const open = externalOpen !== undefined ? externalOpen : internalOpen;
  const setOpen = onOpenChange || setInternalOpen;

  // Fetch available categories when dialog is open
  const categoriesQuery = useMediaLibraryGetCategories({ enabled: !!open });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const categories = categoriesQuery.data?.categories ?? [];

  // API hook for renaming (editing name/category)
  const renameMutation = useMediaLibraryRename({
    onSuccess: () => {
      toast.success('Datei erfolgreich aktualisiert!', {
        description: 'Name und/oder Kategorie wurden geändert.',
        duration: 3000,
      });
      setOpen(false);
      onSuccess?.();
    },
    onError: (error) => {
      console.error('Failed to edit media item:', error);
      toast.error('Fehler beim Bearbeiten', {
        description: 'Die Datei konnte nicht bearbeitet werden. Bitte versuchen Sie es erneut.',
        duration: 5000,
        error,
      });
    },
  });

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open) {
      const nameWithoutExtension = mediaItem.displayName.replace(/\.[^/.]+$/, "");
      setNewName(nameWithoutExtension);
      setSelectedCategories(mediaItem.categories || []);
    } else {
      setNewName("");
      setSelectedCategories([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  /**
   * Handle form submission
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newName.trim()) {
      toast.error('Name erforderlich', {
        description: 'Bitte geben Sie einen neuen Namen für die Datei ein.',
        duration: 3000,
      });
      return;
    }

    // Get file extension from original name
    const extension = mediaItem.displayName.split('.').pop();
    const finalName = extension ? `${newName.trim()}.${extension}` : newName.trim();

    // Check for changes
    const currentCategories = mediaItem.categories || [];
    const categoriesChanged = 
      selectedCategories.length !== currentCategories.length ||
      !selectedCategories.every(cat => currentCategories.includes(cat));

    if (finalName === mediaItem.displayName && !categoriesChanged) {
      toast.info('Keine Änderung', {
        description: 'Name und Kategorie sind identisch mit den aktuellen Werten.',
        duration: 3000,
      });
      setOpen(false);
      return;
    }

    try {
      await renameMutation.mutateAsync({
        id: mediaItem.mediaId,
        displayName: finalName,
        categories: selectedCategories,
      });
    } catch (error) {
      // Error handling is done in the mutation's onError callback
    }
  };

  const isLoading = renameMutation.isPending;
  const fileExtension = mediaItem.displayName.split('.').pop();

  const originalNameWithoutExtension = mediaItem.displayName.replace(/\.[^/.]+$/, "");
  const currentCategories = mediaItem.categories || [];
  
  const hasChanges = 
    newName.trim() !== originalNameWithoutExtension ||
    selectedCategories.length !== currentCategories.length ||
    !selectedCategories.every(cat => currentCategories.includes(cat));

  return (
    <>
      {trigger && (
        <div onClick={() => setOpen(true)} className="inline-block">
          {trigger}
        </div>
      )}
      <CreateBaseDialog
        open={open}
        onClose={() => setOpen(false)}
        icon={Edit3}
        title="Datei bearbeiten"
        description="Name und Kategorie ändern"
        isSaving={isLoading}
        canSave={!!newName.trim() && hasChanges}
        formId="edit-media-form"
        submitLabel="Bearbeiten"
        submitLoadingLabel="Bearbeiten..."
        maxWidth="lg"
      >
        <div className="flex-1 overflow-y-auto px-5 sm:px-6 pb-5 sm:pb-6 pt-2">
          <form id="edit-media-form" onSubmit={handleSubmit} className="space-y-5">
            {/* New Name Input */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Type className="h-4 w-4 text-gray-500" />
                <Label htmlFor="newName" className="text-sm font-medium text-gray-700">
                  Name
                </Label>
                <span className="text-xs text-red-500">*</span>
              </div>
              <div className="relative">
                <Input
                  id="newName"
                  placeholder="Dateiname..."
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  disabled={isLoading}
                  className="h-11 border-gray-200 focus:border-blue-500 focus:ring-blue-500/20 transition-all pr-16"
                />
                {fileExtension && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-500 font-medium">
                    .{fileExtension}
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-500">
                Die Dateiendung wird automatisch beibehalten
              </p>
            </div>

            {/* Category Selection - New Centralized Component */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Tag className="h-4 w-4 text-gray-500" />
                <Label className="text-sm font-medium text-gray-700">
                  Kategorie
                </Label>
              </div>
              <CategorySelector
                categories={categories}
                selected={selectedCategories}
                onChange={setSelectedCategories}
                isLoading={isLoading}
                mode="multiple"
                allowCreate={true}
                placeholder="Kategorien wählen oder erstellen..."
              />
            </div>
          </form>
        </div>
      </CreateBaseDialog>
    </>
  );
};
